import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import charlieCrest from '../assets/icons/charlie-crest.png';
import { THEME_2026 } from '../lib/theme2026';

// Shown when a person opens a password-recovery email link. Supabase has already signed them in with the recovery token (detectSessionInUrl);
// this screen lets them choose the new password, then returns them to the app. The password is never logged or stored by Charlie.
export const MIN_PASSWORD = 12;

// An admin-made link carries ?token_hash=...&type=recovery (no email, no Supabase redirect settings). Read it, take it out of the address bar at once, then trade it for a session.
export const readRecoveryToken = (loc = window.location) => {
  const q = new URLSearchParams(String(loc.search || '').replace(/^\?/, ''));
  const h = new URLSearchParams(String(loc.hash || '').replace(/^#\/?\??/, ''));
  const tokenHash = q.get('token_hash') || h.get('token_hash');
  const type = q.get('type') || h.get('type');
  return tokenHash && type === 'recovery' ? tokenHash : null;
};

export default function PasswordRecovery({ onDone }) {
  const [checking, setChecking] = useState(() => !!readRecoveryToken());
  const [linkError, setLinkError] = useState('');
  useEffect(() => {
    const tokenHash = readRecoveryToken();
    if (!tokenHash) return undefined;
    let live = true;
    try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash && !/token_hash/.test(window.location.hash) ? window.location.hash : ''}`); } catch (_) { /* the link still works */ }
    (async () => {
      let error = null;
      try { ({ error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' })); } catch (e) { error = e || new Error('failed'); }
      if (!live) return;
      if (error) setLinkError('This link did not work. It may have been used already or expired. Ask for a new link.');
      setChecking(false);
    })();
    return () => { live = false; };
  }, []);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (pw.length < MIN_PASSWORD) { setErr(`Use at least ${MIN_PASSWORD} characters.`); return; }
    if (pw !== pw2) { setErr('The two passwords do not match.'); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) { setErr(error.message || 'Could not set the password. Request a new recovery email.'); return; }
    setPw(''); setPw2('');
    setDone(true);
  }

  const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(241,245,249,0.2)', background: 'rgba(0,0,0,0.25)', color: 'var(--gov-slate-100)', fontSize: 14, marginBottom: 12 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: THEME_2026.lapelGradient }}>
      <div style={{ width: '100%', maxWidth: 380, padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src={charlieCrest} alt="CHARLIE" style={{ width: 64, height: 'auto' }} />
        </div>
        <div style={{ background: THEME_2026.cardSidebar, borderRadius: 16, padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gov-slate-100)', marginBottom: 4 }}>Set a new password</div>
          {checking ? (
            <div role="status" style={{ fontSize: 13, color: 'rgba(241,245,249,0.7)', margin: '12px 0 20px' }}>Checking your link</div>
          ) : linkError ? (
            <div role="alert" style={{ color: 'var(--gov-danger-soft)', fontSize: 13, margin: '12px 0 20px' }}>{linkError}</div>
          ) : done ? (
            <>
              <div style={{ fontSize: 13, color: 'rgba(241,245,249,0.7)', margin: '12px 0 20px' }}>Your password is set.</div>
              <button type="button" onClick={onDone} style={{ ...input, cursor: 'pointer', fontWeight: 700, marginBottom: 0 }}>Continue to Charlie</button>
            </>
          ) : (
            <form onSubmit={submit}>
              <div style={{ fontSize: 13, color: 'rgba(241,245,249,0.5)', marginBottom: 20 }}>Choose a password of at least {MIN_PASSWORD} characters.</div>
              <input type="password" autoComplete="new-password" aria-label="New password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} style={input} />
              <input type="password" autoComplete="new-password" aria-label="Confirm new password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} style={input} />
              {err && <div role="alert" style={{ color: 'var(--gov-danger-soft)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
              <button type="submit" disabled={busy} style={{ ...input, cursor: busy ? 'default' : 'pointer', fontWeight: 700, marginBottom: 0 }}>{busy ? 'Saving...' : 'Set password'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
